/**
 * The composed ops router. Registering a new area never requires touching
 * this file.
 *
 * An ops area is discovered by its path, never by name: the glob below
 * matches `routes.ts` in any directory one level up from this one, so
 * registering an area is the single act of creating `src/ops/<area>/routes.ts`
 * with a `bundle` export. A named import list here would be one file every
 * area has to edit, which is exactly the kind of shared file that collides
 * as areas are added independently.
 *
 * Reaches only inside this plane's own tree -- never the tenant application's,
 * whose sibling composition globs its own areas into the TENANT router. An
 * ops area registered there would ship the operator's surface to every tenant
 * and work perfectly, which is why nobody would notice
 * (`src/ops/__tests__/boundary.test.ts` asserts this over the built chunks).
 */

import { createRootRoute, createRoute, createRouter, Outlet } from '@tanstack/react-router';

import { NotFound } from '../../chrome/not-found';
import { bundlesFrom, navFrom } from './compose';

const areaModules = import.meta.glob<Record<string, unknown>>('../*/routes.ts', { eager: true });

// Exported, not just module-local: `OpsShell` reads `opsNav` back from this
// same module (below), so this file's own eager glob is the only safe way to
// discover an area's `routes.ts` -- a second, independent glob over the same
// files from a test recreates the cycle in the opposite order and finds
// `bundle` still unassigned. A test wanting the composed routes reads them
// here instead of re-gathering them.
export const bundles = bundlesFrom(areaModules);

export const opsNav = navFrom(bundles);

// An outlet, not `null`: a root route with no outlet renders no matched
// child at all, whatever the URL -- every route registered below, on this
// plane's own origin. `notFoundComponent` reuses the app plane's `NotFound`
// and its locale keys rather than TanStack Router's own unstyled,
// English-only default.
const rootRoute = createRootRoute({ component: Outlet, notFoundComponent: NotFound });

const routeTree = rootRoute.addChildren(
  bundles.flatMap((bundle) =>
    bundle.routes.map((route) =>
      createRoute({
        getParentRoute: () => rootRoute,
        path: route.path,
        component: () => route.element,
      }),
    ),
  ),
);

export const opsRouter = createRouter({ routeTree });
